import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, generateQueryKey } from "@/lib/api";

export type UserCompany = {
  id: string;
  name: string;
  role: "admin" | "member";
  is_current: boolean;
};

export function useCompanySwitcher() {
  const queryClient = useQueryClient();

  const { data: companies = [], isLoading } = api.useQuery<UserCompany[]>(
    "/companies/",
    {
      refetchOnWindowFocus: true,
    },
  );

  const switchCompanyMutation = useMutation({
    mutationFn: async (companyId: string) => {
      return api.post<UserCompany>("/companies/switch/", {
        company_id: companyId,
      });
    },
    onSuccess: () => {
      // Invalidate queries that depend on the current company
      queryClient.invalidateQueries({
        queryKey: generateQueryKey("/companies/"),
      });

      // Reload the page to apply the new company context
      window.location.reload();
    },
  });

  const switchCompany = (companyId: string) => {
    // Clear all URL parameters before switching companies
    const url = new URL(window.location.href);

    // Remove all search parameters
    url.search = "";

    // Update the URL without triggering a navigation
    window.history.replaceState({}, "", url.toString());

    // Now switch the company
    switchCompanyMutation.mutate(companyId);
  };

  return {
    companies,
    isLoading,
    switchCompany,
    isSwitchingCompany: switchCompanyMutation.isPending,
  };
}
